import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';

import { CoreConfigService } from '@core/services/config.service';
import { CoreMenuService } from '@core/components/core-menu/core-menu.service';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';

@Component({
    selector: 'horizontal-menu',
    templateUrl: './horizontal-menu.component.html',
    styleUrls: ['./horizontal-menu.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class HorizontalMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  coreConfig: any;
  menu: any;

  /** Lista del menú: hace scroll horizontal cuando las opciones no entran */
  @ViewChild('menuList', { read: ElementRef }) menuList: ElementRef<HTMLElement>;

  /** Hay opciones escondidas a la izquierda / a la derecha */
  canScrollPrev = false;
  canScrollNext = false;

  /** Observa el ancho de la lista y las opciones que se agregan al cargar el menú */
  private _resizeObserver: ResizeObserver;
  private _mutationObserver: MutationObserver;

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {CoreConfigService} _coreConfigService
   * @param {CoreMenuService} _coreMenuService
   * @param {CoreSidebarService} _coreSidebarService
   */
  constructor(
    private _coreConfigService: CoreConfigService,
    private _coreMenuService: CoreMenuService,
    private _coreSidebarService: CoreSidebarService,
    private _router: Router,
    private _cdr: ChangeDetectorRef
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On Init
   */
  ngOnInit(): void {
    // Subscribe config change
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;
    });

    // Get current menu
    this._coreMenuService.onMenuChanged
      .pipe(
        filter(value => value !== null),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => {
        this.menu = this._coreMenuService.getCurrentMenu();
      });

    // Al navegar, la opción activa se trae a la vista si había quedado escondida
    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => setTimeout(() => this._revealActive()));
  }

  /**
   * After View Init
   */
  ngAfterViewInit(): void {
    const list = this.menuList?.nativeElement;
    if (!list) return;

    // Las flechas dependen del ancho de la lista y de cuántas opciones tiene:
    // se recalculan si cambia cualquiera de los dos (resize, carga del menú)
    this._resizeObserver = new ResizeObserver(() => this.updateArrows());
    this._resizeObserver.observe(list);

    this._mutationObserver = new MutationObserver(() => this.updateArrows());
    this._mutationObserver.observe(list, { childList: true });

    this.updateArrows();
  }

  // Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Muestra la flecha de cada lado solo si hay opciones escondidas de ese lado
   */
  updateArrows(): void {
    const list = this.menuList?.nativeElement;
    if (!list) return;

    // 1px de tolerancia: el scroll puede quedar en fracciones de píxel
    const prev = list.scrollLeft > 1;
    const next = list.scrollLeft + list.clientWidth < list.scrollWidth - 1;

    if (prev === this.canScrollPrev && next === this.canScrollNext) return;

    this.canScrollPrev = prev;
    this.canScrollNext = next;
    this._cdr.detectChanges();
  }

  /**
   * Desplaza el menú casi un ancho completo, dejando a la vista una opción
   * del tramo anterior para no perder la referencia
   * @param direction -1 hacia la izquierda, 1 hacia la derecha
   */
  scrollMenu(direction: number): void {
    const list = this.menuList?.nativeElement;
    if (!list) return;

    list.scrollBy({ left: direction * list.clientWidth * 0.8, behavior: 'smooth' });
  }

  /** Trae a la vista la opción activa del menú, si quedó fuera */
  private _revealActive(): void {
    const list = this.menuList?.nativeElement;
    const active = list?.querySelector(':scope > li.active') as HTMLElement;
    if (!active) return;

    const listRect = list.getBoundingClientRect();
    const rect = active.getBoundingClientRect();
    if (rect.left >= listRect.left && rect.right <= listRect.right) return;

    list.scrollBy({ left: rect.left - listRect.left - list.clientWidth / 2 + rect.width / 2, behavior: 'smooth' });
  }

  /**
   * On Destroy
   */
  ngOnDestroy(): void {
    this._resizeObserver?.disconnect();
    this._mutationObserver?.disconnect();

    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
